import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SavedPaymentMethods from './SavedPaymentMethods';
import AddPaymentMethodForm from './AddPaymentMethodForm';

interface PaymentMethodManagerProps {
  onSelectPaymentMethod?: (paymentMethodId: string) => void;
  selectedPaymentMethodId?: string;
  mode?: 'selection' | 'management';
  showTitle?: boolean;
}

const PaymentMethodManager: React.FC<PaymentMethodManagerProps> = ({
  onSelectPaymentMethod,
  selectedPaymentMethodId,
  mode = 'management',
  showTitle = true
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleAddSuccess = (paymentMethod: any) => {
    setShowAddForm(false);
    setRefreshKey(prev => prev + 1); // Force refresh of saved payment methods
    
    // If in selection mode, automatically select the new payment method
    if (mode === 'selection' && onSelectPaymentMethod) {
      onSelectPaymentMethod(paymentMethod.id);
    }
  };

  const handleAddCancel = () => {
    setShowAddForm(false);
  };

  return (
    <div className="space-y-6">
      {showTitle && (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {mode === 'selection' ? 'Seleccionar Método de Pago' : 'Gestionar Tarjetas'}
          </h2>
          <p className="text-gray-600">
            {mode === 'selection' 
              ? 'Elige una tarjeta guardada o añade una nueva'
              : 'Administra tus métodos de pago guardados'
            }
          </p>
        </div>
      )}

      <AnimatePresence mode="wait">
        {!showAddForm ? (
          <motion.div
            key={`saved-methods-${refreshKey}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <SavedPaymentMethods
              onSelectPaymentMethod={onSelectPaymentMethod}
              onAddNewCard={() => setShowAddForm(true)}
              selectedPaymentMethodId={selectedPaymentMethodId}
              showAddButton={true}
            />
          </motion.div>
        ) : (
          <motion.div
            key="add-form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <AddPaymentMethodForm
              onSuccess={handleAddSuccess}
              onCancel={handleAddCancel}
              setAsDefault={false}
              showSetAsDefaultOption={true}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Actions for Management Mode */}
      {mode === 'management' && !showAddForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gray-50 rounded-2xl p-6"
        >
          <h4 className="font-semibold text-gray-900 mb-4">Información Importante</h4>
          <div className="space-y-3 text-sm text-gray-600">
            <div className="flex items-start gap-3">
              <span className="text-yellow-500">💳</span>
              <p>
                Puedes guardar múltiples tarjetas para hacer tus compras más rápidas y convenientes.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-green-500">🔒</span>
              <p>
                Toda la información de tus tarjetas está cifrada y protegida. No almacenamos los datos completos de tu tarjeta.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-blue-500">⚡</span>
              <p>
                La tarjeta marcada como "por defecto" se usará automáticamente en tus próximas compras.
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PaymentMethodManager;